/**
 * Relatórios > Financeiro > Recebíveis PIX.
 *
 * Um registro por PIX confirmado no PaymentHub: o lançamento criado
 * automaticamente no Conta Corrente, a taxa PREVISTA (snapshot do cadastro de
 * taxas PIX) e a confirmação do crédito pelo extrato do Itaú.
 *
 * O cabeçalho traz o comparativo do mês: soma das taxas previstas × tarifa
 * consolidada que o Itaú cobrou ("Tarifa bancaria - Pix qrcode") — o banco
 * credita cada PIX cheio e cobra a tarifa uma vez por mês, então a conferência
 * da taxa só existe nesta granularidade.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Clock, RefreshCw } from 'lucide-react';
import { companyService } from '@services/companyService';
import { financialService } from '@services/financialService';
import { formatCurrencyBRL } from '@pages/financial/financialUtils';
import { LoadingSpinner, ErrorBox, SummaryCard, selectCls, inputCls } from '@pages/financial/financialShared';
import type { Company } from '@/types/company';
import type { PixReceivable, PixFeeSummary } from '@/types/financial';

const currentMonth = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const formatPaidAt = (paidAt?: string): string => {
  if (!paidAt) return '—';
  const [date, time] = paidAt.split('T');
  const [y, m, d] = date.split('-');
  return `${d}/${m}/${y}${time ? ` ${time.slice(0, 5)}` : ''}`;
};

const formatDateBRFromISO = (iso?: string): string => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const monthRange = (month: string): { from: string; to: string } => {
  const [y, m] = month.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
};

export const PixReceivablesView: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [month, setMonth] = useState(currentMonth());

  const [receivables, setReceivables] = useState<PixReceivable[]>([]);
  const [summary, setSummary] = useState<PixFeeSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    companyService
      .getCompanies()
      .then((list) => {
        setCompanies(list);
        if (list.length > 0) setCompanyId((prev) => prev ?? list[0].company_id);
      })
      .catch(() => setError('Erro ao carregar empresas'));
  }, []);

  const load = useCallback(async () => {
    if (companyId == null || !/^\d{4}-\d{2}$/.test(month)) return;
    setLoading(true);
    setError(null);
    try {
      const { from, to } = monthRange(month);
      const [list, fees] = await Promise.all([
        financialService.getPixReceivables({ companyId, from, to }),
        financialService.getPixFeeSummary(companyId, month),
      ]);
      setReceivables(list);
      setSummary(fees);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar recebíveis PIX');
    } finally {
      setLoading(false);
    }
  }, [companyId, month]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs text-slate-500 mb-1">Empresa</label>
          <select
            className={selectCls + ' w-64'}
            value={companyId ?? ''}
            onChange={(e) => setCompanyId(e.target.value ? Number(e.target.value) : null)}
          >
            {companies.map((c) => (
              <option key={c.company_id} value={c.company_id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Mês</label>
          <input type="month" className={inputCls + ' w-44'} value={month} onChange={(e) => setMonth(e.target.value)} />
        </div>
        <button
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50"
          onClick={load}
          disabled={loading}
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Atualizar
        </button>
      </div>

      {error && <ErrorBox message={error} />}

      {/* Comparativo mensal da tarifa */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard label="PIX recebidos no mês" value={String(summary.pixCount)} />
          <SummaryCard label="Total bruto" value={formatCurrencyBRL(summary.grossCents / 100)} />
          <SummaryCard
            label="Taxa prevista (soma)"
            value={formatCurrencyBRL(summary.expectedFeeCents / 100)}
            hint="Estimada pelo cadastro de taxas PIX, por transação"
          />
          <SummaryCard
            label="Tarifa cobrada pelo Itaú"
            value={summary.chargedFeeCents > 0 ? formatCurrencyBRL(summary.chargedFeeCents / 100) : '—'}
            hint={
              summary.chargedFeeCents > 0
                ? `Lançamento de ${formatDateBRFromISO(summary.chargedDate)} — consolidada mensal, cobrada no mês seguinte`
                : 'Consolidada mensal; aparece quando o débito "Pix qrcode" for lançado'
            }
          />
        </div>
      )}

      {loading && <LoadingSpinner />}

      {!loading && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-2 px-3 font-semibold text-slate-600">Pagamento</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-600">Pedido / Proposta</th>
                  <th className="text-right py-2 px-3 font-semibold text-slate-600">Valor bruto</th>
                  <th className="text-right py-2 px-3 font-semibold text-slate-600">Taxa prevista</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-600">Lançamento</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-600">Extrato</th>
                </tr>
              </thead>
              <tbody>
                {receivables.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-10 text-center text-slate-400">
                      Nenhum recebimento PIX no período
                    </td>
                  </tr>
                )}
                {receivables.map((r) => (
                  <tr key={r.pixReceivableId} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-3 text-slate-700 whitespace-nowrap">{formatPaidAt(r.paidAt)}</td>
                    <td className="py-2 px-3 text-slate-700">
                      {r.orderId != null
                        ? `Pedido ${r.orderId}`
                        : r.comercialProposalId != null
                          ? `Proposta ${r.comercialProposalId}`
                          : `TxId ${r.txId.slice(0, 12)}…`}
                    </td>
                    <td className="py-2 px-3 text-right text-slate-900 font-medium whitespace-nowrap">
                      {formatCurrencyBRL(r.grossAmountCents / 100)}
                    </td>
                    <td
                      className="py-2 px-3 text-right text-slate-500 whitespace-nowrap"
                      title={r.acquirerPixTaxId == null ? 'Nenhuma taxa cadastrada casou com este EC/CNPJ' : undefined}
                    >
                      {formatCurrencyBRL(r.feeAmountCents / 100)}
                      {r.feePercent > 0 && (
                        <span className="text-xs text-slate-400"> ({r.feePercent.toLocaleString('pt-BR')}%)</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-slate-500 whitespace-nowrap">
                      {r.financialTransactionId != null ? `#${r.financialTransactionId}` : (
                        <span className="text-amber-600">pendente</span>
                      )}
                    </td>
                    <td className="py-2 px-3 whitespace-nowrap">
                      {r.status === 1 ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <CheckCircle2 size={12} /> Confirmado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-slate-50 text-slate-500 border border-slate-200">
                          <Clock size={12} /> Aguardando
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PixReceivablesView;
